/**
 * SPRINT V4.2 — Daily WOW Splash.
 * Shown once per day on first app open. Max ~1.5s. Shows: bandera,
 * ranking, racha, puntos de ayer, rival principal.
 */
import { useEffect, useState } from "react";
import { useSelectedNation } from "@/hooks/useSelectedNation";
import { getNationFlag, getNationName } from "@/lib/share/nationWarsShare";
import { loadStreak } from "@/lib/retention/streaks";
import { trackEvent } from "@/lib/audit";
import { pickFoundationDailyWow } from "@/lib/demo/executiveDemoMock";
import { sponsorFor } from "@/lib/sponsor/sponsorMoments";
import { SponsorMomentBadge } from "@/components/sponsor/SponsorMomentBadge";
import { VictoryCardCta } from "@/components/viral/VictoryCardCta";
import { fireVictoryPrompt } from "@/lib/viral/victoryPromptBus";
import { registerFab } from "@/lib/ui/fabOrchestrator";
import { openShareSheet } from "@/components/viral/ShareSheetHost";

const KEY = "vs:v4:wow:lastShownDay";
const DURATION_MS = 1500;
const CARD_DISMISS_KEY = "vs:v4:wow:cardDismissedDay";

function todayKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}
function shownToday(): boolean {
  try { return localStorage.getItem(KEY) === todayKey(); } catch { return false; }
}
function markShown() {
  try { localStorage.setItem(KEY, todayKey()); } catch { /* noop */ }
}
function pointsYesterday(): number {
  try { return Number(localStorage.getItem("vs:v4:wow:ptsYesterday") ?? 0); } catch { return 0; }
}

export function DailyWowSplash() {
  const { nation } = useSelectedNation();
  const [visible, setVisible] = useState(false);
  const [cardVisible, setCardVisible] = useState(false);

  // P0-RETENTION-01 — register with the FAB orchestrator while either
  // the splash or the persistent "Tu momento del día" card is on screen
  // so other floating elements step aside.
  useEffect(() => {
    if (!visible && !cardVisible) return;
    const release = registerFab("daily_wow_card");
    return release;
  }, [visible, cardVisible]);

  useEffect(() => {
    const already = shownToday();
    if (!already) {
      markShown();
      setVisible(true);
    }
    // Persistent "Tu momento del día" card — shows once per day until dismissed.
    try {
      if (localStorage.getItem(CARD_DISMISS_KEY) !== todayKey()) {
        setCardVisible(true);
      }
    } catch { /* noop */ }
    const streak = loadStreak();
    void trackEvent("daily_wow_shown", {
      nation, streak: streak.consecutive_days_active,
    });
    const t = already ? null : window.setTimeout(() => setVisible(false), DURATION_MS);
    // P0-DISCOVERY-01 — surface the non-blocking VictoryCardPrompt
    // after the splash fades instead of forcing the ShareSheet open.
    if (!already) {
      window.setTimeout(
        () => fireVictoryPrompt({ source: "daily_wow" }),
        DURATION_MS + 600,
      );
    }
    return () => { if (t) window.clearTimeout(t); };
  }, [nation]);

  if (!visible && !cardVisible) return null;
  const streak = loadStreak();
  const rank = 3; // best-effort placeholder until live ranking is wired
  const rivalCode = nation === "MEX" ? "ARG" : "MEX";
  const yesterday = pointsYesterday();
  // WORLD-CUP-LAUNCH-P2 — editorial fallback so the splash is never empty.
  const editorial = pickFoundationDailyWow(`wow-${nation ?? "ALL"}`);
  const sponsor = sponsorFor("nation_dominance");

  return (
    <>
    {visible && (
    <div
      role="dialog"
      aria-label="Daily WOW"
      className="pointer-events-none fixed inset-0 z-[100] flex items-center justify-center bg-black/90 animate-fade-in"
    >
      <div className="flex flex-col items-center gap-2 text-center animate-scale-in">
        <div className="text-7xl drop-shadow-lg">{getNationFlag(nation)}</div>
        <div className="text-xl font-black uppercase tracking-widest text-primary">
          {getNationName(nation)}
        </div>
        <div className="grid grid-cols-3 gap-3 pt-3 text-foreground">
          <Cell label="Ranking" value={`#${rank}`} />
          <Cell label="Racha" value={`${streak.consecutive_days_active}🔥`} />
          <Cell label="Ayer" value={`+${yesterday}`} />
        </div>
        <div className="pt-2 text-[11px] uppercase tracking-widest text-muted-foreground">
          Rival · {getNationFlag(rivalCode)} {getNationName(rivalCode)}
        </div>
        <div className="mt-2 max-w-[280px] rounded-xl border border-border/60 bg-background/60 px-3 py-2">
          <div className="text-[9px] uppercase tracking-widest text-muted-foreground">
            Momento del día · editorial
          </div>
          <div className="text-sm font-black leading-tight">
            {editorial.emoji} {editorial.headline}
          </div>
          <div className="text-[10px] text-muted-foreground">{editorial.subline}</div>
        </div>
        {sponsor && (
          <div className="pointer-events-auto">
            <SponsorMomentBadge sponsor={sponsor} />
          </div>
        )}
        <div className="pointer-events-auto pt-1">
          <VictoryCardCta source="daily_wow" compact />
        </div>
      </div>
    </div>
    )}
    {!visible && cardVisible && (
      <div
        role="dialog"
        aria-label="Tu momento del día"
        className="pointer-events-auto fixed bottom-[calc(env(safe-area-inset-bottom,0px)+96px)] left-1/2 z-[80] w-[min(92vw,360px)] -translate-x-1/2 rounded-2xl border border-primary/40 bg-background/95 p-3 shadow-xl backdrop-blur-md animate-fade-in"
      >
        <div className="flex items-start gap-3">
          <div className="text-3xl leading-none">{getNationFlag(nation)}</div>
          <div className="min-w-0 flex-1">
            <div className="text-[9px] uppercase tracking-widest text-primary">Tu momento del día</div>
            <div className="truncate text-sm font-black leading-tight">
              {editorial.emoji} {editorial.headline}
            </div>
            <div className="mt-0.5 truncate text-[10px] text-muted-foreground">
              Racha {streak.consecutive_days_active}🔥 · +{yesterday} ayer
            </div>
          </div>
          <button
            type="button"
            aria-label="Cerrar"
            onClick={() => {
              try { localStorage.setItem(CARD_DISMISS_KEY, todayKey()); } catch { /* noop */ }
              setCardVisible(false);
            }}
            className="text-muted-foreground hover:text-foreground"
          >×</button>
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => {
              // AUDIT-UX-02 — the previous handler only fired a window
              // event picked up by VictoryCardPrompt, which is mounted
              // inside RetentionFab (Home + LIVE only). On /welcome and
              // most routes nothing listens, so the click had no visible
              // effect. Open the globally-mounted ShareSheet directly,
              // and still emit the event for surfaces that do listen.
              void trackEvent("daily_wow_card_action", { action: "share_clicked" });
              try {
                void trackEvent("daily_wow_card_action", { action: "share_started" });
                openShareSheet();
                fireVictoryPrompt({ source: "daily_wow" });
                void trackEvent("daily_wow_card_action", { action: "share_success" });
              } catch (err) {
                void trackEvent("daily_wow_card_action", {
                  action: "share_failed",
                  error: err instanceof Error ? err.message : String(err),
                });
              }
            }}
            className="rounded-lg bg-primary px-3 py-1.5 text-xs font-black text-primary-foreground"
          >
            Compartir mi día
          </button>
          <a
            href="/quinielas"
            onClick={() => void trackEvent("daily_wow_card_action", { action: "prediction" })}
            className="rounded-lg border border-border bg-background px-3 py-1.5 text-center text-xs font-black"
          >
            Ver predicción de hoy
          </a>
        </div>
      </div>
    )}
    </>
  );
}

function Cell({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border/60 bg-background/60 px-3 py-2">
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-lg font-black tabular-nums">{value}</div>
    </div>
  );
}